import { Controller } from "@hotwired/stimulus"
import {
  safeLocalStorage,
  settingsNavigationStorageKey,
  readExpandedSettingsSections,
  writeExpandedSettingsSections,
} from "../utils/settings_navigation_state"

export default class extends Controller {
  static targets = ["section", "toggle", "panel"]
  static values = { userId: String }

  connect() {
    this.allowedIds = this.sectionTargets.map((section) => section.dataset.settingsSectionId)
    this.storageKey = settingsNavigationStorageKey({
      host: window.location.host,
      userId: this.userIdValue,
    })
    this.expandedIds = new Set(readExpandedSettingsSections({
      storage: safeLocalStorage(),
      key: this.storageKey,
      allowedIds: this.allowedIds,
    }))
    // The group holding the current page opens on its own (#18871), so the red
    // "you are here" marker lands on the page link, not on a closed header.
    // Kept apart from expandedIds: it is never saved as the agent's choice.
    // activeSectionKey remembers which groups held the current page the last
    // time it was applied, so only a move to a different group re-opens one.
    this.activeIds = new Set()
    this.activeSectionKey = null
    this.boundSyncActiveLinks = () => this.syncActiveLinks()
    document.addEventListener("turbo:frame-load", this.boundSyncActiveLinks)
    window.addEventListener("popstate", this.boundSyncActiveLinks)
    this.syncActiveLinks()
  }

  disconnect() {
    document.removeEventListener("turbo:frame-load", this.boundSyncActiveLinks)
    window.removeEventListener("popstate", this.boundSyncActiveLinks)
  }

  toggle(event) {
    const section = event.currentTarget.closest("[data-settings-section-id]")
    const id = section.dataset.settingsSectionId

    if (this.isExpanded(id)) {
      this.expandedIds.delete(id)
      this.activeIds.delete(id)
    } else {
      this.expandedIds.add(id)
    }
    this.applySectionState()
    writeExpandedSettingsSections({
      storage: safeLocalStorage(),
      key: this.storageKey,
      expandedIds: [...this.expandedIds],
    })
  }

  prepareProfileNavigation(event) {
    if (document.getElementById("settings_panel")) {
      event.currentTarget.dataset.turboFrame = "settings_panel"
      event.currentTarget.dataset.turboAction = "advance"
    } else {
      delete event.currentTarget.dataset.turboFrame
      delete event.currentTarget.dataset.turboAction
    }
  }

  applySectionState() {
    this.sectionTargets.forEach((section) => {
      const expanded = this.isExpanded(section.dataset.settingsSectionId)
      const toggle = section.querySelector('[data-settings-navigation-target="toggle"]')
      const panel = section.querySelector('[data-settings-navigation-target="panel"]')

      section.classList.toggle("is-expanded", expanded)
      section.classList.toggle("is-collapsed", !expanded)
      toggle.setAttribute("aria-expanded", String(expanded))
      panel.hidden = !expanded
    })
  }

  syncActiveLinks() {
    const currentPath = window.location.pathname.replace(/\/$/, "") || "/"

    this.element.querySelectorAll(".gl-settings-nav__profile-link[href]").forEach((link) => {
      const linkPath = new URL(link.href, window.location.origin).pathname.replace(/\/$/, "") || "/"
      link.classList.toggle("active", linkPath === currentPath)
    })
    const activeIds = []
    this.sectionTargets.forEach((section) => {
      const active = Boolean(section.querySelector("a.active"))
      section.classList.toggle("has-active-destination", active)
      if (active) activeIds.push(section.dataset.settingsSectionId)
    })
    // Auto-open only when the current page moved to a different group. An
    // unrelated turbo:frame-load or popstate on the same page must not re-open
    // a group the agent closed by hand.
    const key = activeIds.join("|")
    if (key !== this.activeSectionKey) {
      this.activeSectionKey = key
      this.activeIds = new Set(activeIds)
    }
    this.applySectionState()
  }

  isExpanded(id) {
    return this.expandedIds.has(id) || this.activeIds.has(id)
  }
}
